import React from 'react';
import Highcharts, { Point } from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { css } from '@emotion/react';
import { IGetDfreeBDeviceAnalyticsCareRecord } from '../../../../types/api/traversal/getDfreeBDeviceAnalytics';
import { useChartResizeObserver } from '../../../../hooks/useChartResizeObserver';
import dayjs, { Dayjs } from 'dayjs';
import { DEFECATION_ANALYTICS } from '../../../../constants/defecationAnalytics';
import { color } from '../../../../styles';
import { useTranslation } from 'react-i18next';

interface Props {
  startDate: string;
  careRecords?: IGetDfreeBDeviceAnalyticsCareRecord[];
}

type defecationTypeKeys = Exclude<keyof typeof DEFECATION_ANALYTICS.VOLUME_TYPES_JA, 'none'>;
type DefecationTypeCount = Record<defecationTypeKeys, number>;

const GraphContainerStyle = css`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 24px 0;
`;

const GraphContainer = css`
  position: relative;
  width: 100%;
`;

const GraphXLabelContainer = (leftSpacing?: number, width?: number) => css`
  padding-bottom: 10px;
  margin-left: ${leftSpacing ? `${leftSpacing}px` : '78px'};
  width: ${width ? `${width}px` : 'calc(100% - 78px)'};
  display: flex;
`;

const GraphXLabelItem = css`
  flex: 1;
  font-size: 14px;
  color: ${color.gray};
  text-align: center;
`;

const BarChartContainer = css`
  width: 100%;
  border-top: 1px solid ${color.border_gray};
  border-bottom: 1px solid ${color.border_gray};
`;

const TableContainer = css`
  width: 100%;
  background-color: #fffcf4;
  border-bottom: 1px solid ${color.border_gray};
`;

const Table = css`
  width: 100%;
`;

const TableTitle = css`
  width: fit-content;
  padding: 0 20px 0 8px;
  writing-mode: vertical-rl;
  font-size: 14px;
  color: ${color.dark_gray};
`;

const TableCell = (
  width: number, // ベースのセルサイズ（先頭セルには+5pxする）
  dateCount: number,
  isFirst: boolean, // 先頭のセルかどうか
  isLast: boolean, // 最後のセルかどうか
  tableHeaderWidth: number // テーブルタイトル幅＋行のタイトル幅
) => css`
  ${isLast
    ? `width: 100%`
    : isFirst
    ? `min-width: ${width + 5}px; max-width: ${width + 5}px`
    : `min-width: ${width}px; max-width: ${width}px`};
  ${isFirst && 'padding-left: 5px'};
  // 一番右の要素に右マージンをセットする
  // テーブルサイズ - （データが入っているエリア + テーブルタイトル + 行のタイトル）
  ${isLast && `padding-right: calc(100% - ${width * dateCount + 5 + tableHeaderWidth}px)`}
`;

const TableCellBorder = css`
  border-bottom: 1px dotted ${color.border_gray};
`;

const TableRowTitle = css`
  width: fit-content;
  padding: 4px 8px;
  color: ${color.gray};
  font-size: 12px;
  font-weight: normal;
`;

const DefecationCount = css`
  margin: 0 auto;
  width: auto;
  max-width: 100%;
  height: calc(1em + 6px);
  max-height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  aspect-ratio: 1;
  font-size: 12px;
  line-height: 1em;
  color: ${color.white};
  background-color: ${color.brown};
  border-radius: 50vh;
`;

// 月の初日は "月/日" 形式
// それ以外は "日" だけ
const formatDate = (i: number, date: Dayjs) => {
  if (i === 0 || date.date() === 1) {
    return date.format('M/D');
  } else {
    return date.format('D');
  }
};

export const DefecationDrinkEatRelationChart: React.FC<Props> = ({ startDate, careRecords }) => {
  const { t, i18n } = useTranslation();
  const chartRef = React.useRef<HighchartsReact.RefObject>(null);
  useChartResizeObserver(chartRef);
  // コールバックで取得したオプションを保持する
  const [plotWidth, setPlotWidth] = React.useState<number>(0);

  const tableTitleRef = React.useRef<HTMLTableCellElement>(null);
  const tableRowTitleRef = React.useRef<HTMLTableCellElement>(null);
  const [tableHeaderWidth, setTableHeaderWidth] = React.useState<number>(0);

  const start = dayjs(startDate);
  const dateCount = 30;
  const dates: string[] = Array.from({ length: dateCount }, (_, i) => start.add(i, 'day').format('M/D'));

  const defecationData: DefecationTypeCount[] = React.useMemo(() => {
    const initCount: DefecationTypeCount = Object.fromEntries(
      Object.keys(DEFECATION_ANALYTICS.VOLUME_TYPES_JA)
        .filter((key) => key !== 'none')
        .map((key) => [key, 0])
    ) as DefecationTypeCount;
    return Array.from({ length: dateCount }, (_, i) => {
      const filteredRecords = careRecords?.filter(
        (v) =>
          start.add(i, 'day').isSame(v.timestamp, 'day') &&
          !!v.defecationVolumeType &&
          v.defecationVolumeType !== 'none'
      );
      return filteredRecords && filteredRecords.length > 0
        ? filteredRecords.reduce(
            (acc: DefecationTypeCount, current: IGetDfreeBDeviceAnalyticsCareRecord) => {
              if (current.defecationVolumeType in acc) {
                acc[current.defecationVolumeType as defecationTypeKeys]++;
              }
              return acc;
            },
            { ...initCount } // initCountをそのまま渡すと参照渡しとなり値が更新されてしまうので、新しいオブジェクトとして渡す
          )
        : initCount;
    });
  }, [careRecords]);

  const options: Highcharts.Options = React.useMemo(() => {
    // TODO: 食事量
    // const mealData = Array.from({ length: dateCount }, (_, i) => Math.floor(Math.random() * (10 - 0)) + 0);
    // 飲水
    const drinkData: number[] = Array.from({ length: dateCount }, (_, i) => {
      const filteredRecords = careRecords?.filter((v) => start.add(i, 'day').isSame(v.timestamp, 'day'));
      return filteredRecords
        ? filteredRecords.reduce(
            (acc: number, current: IGetDfreeBDeviceAnalyticsCareRecord) => acc + (current.drinkingVolume ?? 0),
            0
          )
        : 0;
    });
    return {
      chart: {
        type: 'column',
        height: 142,
        backgroundColor: 'transparent',
        spacing: [0, 0, 0, 0],
      },
      title: { text: '' },
      tooltip: {
        shared: true, // 両方の系列を同時に表示（食事量が入ってきてもtooltipはそのまま使える）
        formatter: function () {
          return `
            ${this.x}
            ${this.points
              ?.map(
                (point) =>
                  `<br/>${point.series.name}: <b>${point.y?.toLocaleString()} ${
                    point.series.options.custom?.unit ?? ''
                  }</b>`
              )
              .join('')}
          `;
        },
      },
      xAxis: {
        title: {
          text: '',
        },
        labels: {
          enabled: false,
        },
        tickLength: 0,
        categories: dates,
      },
      yAxis: [
        // まだ食事量はないけどこれを記述しておかないとレイアウトが崩れる（グラフが左寄せになる）
        // TODO: 食事量が入ってきたらtransparentを変更する
        {
          min: 0,
          max: 10,
          title: {
            text: t('mealSize', '食事量'),
            style: {
              color: 'transparent',
              fontFamily: 'Noto Sans JP',
              fontWeight: 'bold',
              fontSize: '14px',
              writingMode: 'vertical-rl',
              transform: 'rotate(360deg)',
            },
          },
          labels: {
            enabled: true,
            style: {
              color: 'transparent',
            },
          },
          offset: -2,
          gridLineWidth: 0,
          opposite: false,
        }, // 左軸
        {
          min: 0,
          max: 1500,
          title: {
            text: t('waterIntake', '飲水'),
            style: {
              fontFamily: 'Noto Sans JP',
              fontWeight: 'bold',
              fontSize: '14px',
              writingMode: 'vertical-rl',
              transform: 'rotate(360deg)',
            },
          },
          labels: {
            enabled: true,
            formatter: function () {
              if (this.value === 0) {
                return '';
              }
              if (this.value === 1500) {
                return `<tspan dy="10">${this.value + 'ml'}</tspan>`;
              }
              return this.value + 'ml';
            },
          },
          tickPositions: [0, 750, 1500],
          plotLines: [
            {
              value: 750,
              width: 1,
              color: color.border_gray,
              dashStyle: 'Dot',
            },
          ],
          offset: -5,
          gridLineWidth: 0,
          opposite: true,
        },
      ],
      series: [
        // TODO: 食事量が入ってきたらコメントアウトを外す
        // { name:  t('mealSize', '食事量'), type: 'column', data: mealData, yAxis: 0, color: '#9BD96A' },
        {
          name: t('waterIntake', '飲水'),
          type: 'column',
          data: drinkData,
          yAxis: 1,
          color: '#7BD6E7',
          custom: { unit: 'ml' },
        },
      ],
      // TODO: 食事量が入ってきたらコメントアウトを外す
      // plotOptions: {
      //   column: {
      //     pointPadding: 0.2,
      //     groupPadding: 0.1,
      //     pointWidth: 8,
      //   },
      // },
      legend: {
        enabled: false,
      },
      credits: {
        enabled: false,
      },
    };
  }, [careRecords]);

  const chartCallback = (chart: Highcharts.Chart) => {
    setPlotWidth(chart.plotWidth);

    // resize イベントを検知して再取得
    Highcharts.addEvent(chart, 'resize', () => {
      setPlotWidth(chart.plotWidth);
    });
  };

  React.useEffect(() => {
    // テーブルタイトル + 行のタイトルの幅合計をセット（スタイリング用）
    setTableHeaderWidth((tableTitleRef.current?.offsetWidth ?? 0) + (tableRowTitleRef.current?.offsetWidth ?? 0));

    // クリーンアップ処理
    return () => {
      Highcharts.removeEvent(Highcharts.chart);
    };
  }, []);

  return (
    <div css={GraphContainerStyle}>
      <div css={GraphContainer}>
        <div css={GraphXLabelContainer(tableHeaderWidth, plotWidth ?? 600)}>
          {dates.map((v, idx) => (
            <div key={idx} css={GraphXLabelItem}>
              {formatDate(idx, dayjs(v))}
            </div>
          ))}
        </div>
        <div css={BarChartContainer}>
          <HighchartsReact highcharts={Highcharts} options={options} callback={chartCallback} ref={chartRef} />
        </div>
        <div css={TableContainer}>
          <table css={Table}>
            <tbody>
              <tr>
                <th rowSpan={4} ref={tableTitleRef} css={TableTitle}>
                  {t('defecation', '排便')}
                </th>
                <th ref={tableRowTitleRef} css={TableRowTitle}>
                  {t('large', '多')}
                </th>
                {defecationData.map((v, idx) => (
                  <td
                    key={idx}
                    css={[
                      TableCell(plotWidth / dateCount, dateCount, idx === 0, idx === dateCount - 1, tableHeaderWidth),
                      TableCellBorder,
                    ]}
                  >
                    {v.large > 0 && <div css={DefecationCount}>{v.large}</div>}
                  </td>
                ))}
              </tr>
              <tr>
                <th css={TableRowTitle}>{t('medium', '中')}</th>
                {defecationData.map((v, idx) => (
                  <td
                    key={idx}
                    css={[
                      TableCell(plotWidth / dateCount, dateCount, idx === 0, idx === dateCount - 1, tableHeaderWidth),
                      TableCellBorder,
                    ]}
                  >
                    {v.medium > 0 && <div css={DefecationCount}>{v.medium}</div>}
                  </td>
                ))}
              </tr>
              <tr>
                <th css={TableRowTitle}>{t('small', '少')}</th>
                {defecationData.map((v, idx) => (
                  <td
                    key={idx}
                    css={[
                      TableCell(plotWidth / dateCount, dateCount, idx === 0, idx === dateCount - 1, tableHeaderWidth),
                      TableCellBorder,
                    ]}
                  >
                    {v.small > 0 && <div css={DefecationCount}>{v.small}</div>}
                  </td>
                ))}
              </tr>
              <tr>
                <th css={TableRowTitle}>{t('bit', '付')}</th>
                {defecationData.map((v, idx) => (
                  <td
                    key={idx}
                    css={TableCell(
                      plotWidth / dateCount,
                      dateCount,
                      idx === 0,
                      idx === dateCount - 1,
                      tableHeaderWidth
                    )}
                  >
                    {v.bit > 0 && <div css={DefecationCount}>{v.bit}</div>}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
